/*
 * Cài đặt Telesale — gom các cài đặt đang nằm rải rác về một chỗ (họp 30/10):
 * nhóm dịch vụ & chia số, ngưỡng vận hành (bỏ quên theo từng trạng thái), chia lại khách quá hạn, quy tắc chuyển trạng thái.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Checkbox, Input, InputNumber, Select, Switch, Table, Tabs, Tag, message } from 'antd';
import { ArrowRight, Settings2 } from 'lucide-react';
import Layout from '../../Layout';
import useAuthStore from '../../store/authStore';
import { mixGetV1 } from '../../services/mixin';
import { getCustomerTags, type CustomerTagItem } from '../../api/CustomerTag';
import { AGENTS } from './dashboard/data';
import { DEFAULT_CFG, FORGOT_STAGES, type Cfg } from './dashboard/logic';
import useTeleDash from './dashboard/store';
import { Card } from './dashboard/ui';
import { GROUPS, groupByKey, groupOfText, useGroupCfg, type GroupKey } from './lead/groups';
import { useLeadStore } from './lead/store';
import { thresholdText } from './lead/reassign';
import ReassignSettings from './lead/ReassignSettings';

interface StaffRow { id: string; name: string; hint?: string }

/** Telesale trên hệ thống; chưa tải được thì dùng nhân viên mẫu của Dashboard */
function useTeleStaffs(): StaffRow[] {
  const [api, setApi] = useState<StaffRow[]>([]);
  useEffect(() => {
    mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 3 })
      .then((res: any) => {
        const items = Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : [];
        setApi(items.filter((s: any) => s?.id).map((s: any) => ({ id: String(s.id), name: s.fullname || s.name || `#${s.id}` })));
      })
      .catch(() => undefined);
  }, []);
  return api.length ? api : AGENTS.map((a) => ({ id: a.id, name: a.name, hint: 'Nhân viên mẫu (Dashboard)' }));
}

/* ---------------- Nhóm dịch vụ & chia số ---------------- */

function GroupsTab() {
  const { cfg, setCfg, reset } = useGroupCfg();
  const staffs = useTeleStaffs();
  const [probe, setProbe] = useState('');
  const hit = probe.trim() ? groupByKey(groupOfText(probe, cfg)) : undefined;
  const setStaff = (id: string, gs: GroupKey[]) => setCfg({ staff: { ...cfg.staff, [id]: gs } });
  const setRatio = (id: string, x: number | null) => setCfg({ ratio: { ...cfg.ratio, [id]: x ?? 0 } });
  // Tổng tỉ lệ từng nhóm (nên = 100%)
  const sums = GROUPS.map((g) => ({ g, sum: staffs.filter((s) => (cfg.staff[s.id] ?? []).includes(g.key)).reduce((a, s) => a + (cfg.ratio[s.id] ?? 0), 0) }));

  return (
    <div className="space-y-4">
      <Card title="Chia số theo nhóm dịch vụ">
        <div className="space-y-3 text-[13px]">
          <label className="flex items-center justify-between gap-3">
            <span><b>Bật chia theo nhóm</b> — data thuộc nhóm nào chỉ chia cho telesale của nhóm đó (chia tuần tự / ít khách nhất trong nhóm). Không ai cùng nhóm thì giữ nguyên, không đẩy sang nhóm khác.</span>
            <Switch checked={cfg.enabled} onChange={(x) => setCfg({ enabled: x })} />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span><b>Ưu tiên bù số</b> — người đang ít khách so với tỉ lệ yêu cầu được nhận trước (người 40% nhận gấp đôi người 20%).</span>
            <Switch checked={cfg.compensate} onChange={(x) => setCfg({ compensate: x })} />
          </label>
        </div>
      </Card>

      <Card title="Dịch vụ thuộc từng nhóm" extra={<Button size="small" type="link" onClick={reset}>Khôi phục mặc định</Button>}>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          {GROUPS.map((g) => (
            <div key={g.key} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <div className="mb-1 flex items-center justify-between gap-2">
                <Tag color={g.color} className="!m-0 !text-[13px] !font-semibold">{g.label}</Tag>
                <span className="text-xs text-gray-400">mảng {g.mangs.join(', ')}</span>
              </div>
              <Select mode="tags" value={cfg.svc[g.key]} onChange={(x) => setCfg({ svc: { ...cfg.svc, [g.key]: x } })} className="w-full" placeholder="Gõ tên dịch vụ / từ khoá, Enter để thêm" tokenSeparators={[',']} />
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[13px]">
          <span className="text-gray-500">Thử nhận nhóm:</span>
          <Input value={probe} onChange={(e) => setProbe(e.target.value)} placeholder="VD: em ơi tư vấn cho chị Ultherapy" className="!w-[320px]" allowClear />
          {probe.trim() && (hit ? <Tag color={hit.color}>{hit.label}</Tag> : <Tag>Chưa nhận ra — trực page chọn nhóm tay</Tag>)}
        </div>
        <p className="mt-2 text-xs text-gray-500">Hệ thống đọc dịch vụ quan tâm, vấn đề khách nêu, ghi chú trực page và trang nguồn; không khớp thì lấy theo mảng của data.</p>
      </Card>

      <Card title="Telesale thuộc nhóm nào · tỉ lệ yêu cầu">
        <Table<StaffRow> rowKey="id" size="small" pagination={false} dataSource={staffs}
          columns={[
            { title: 'Telesale', key: 'n', render: (_, s) => <span><b>{s.name}</b>{s.hint && <span className="ml-1 text-xs text-gray-400">· {s.hint}</span>}</span> },
            {
              title: 'Nhận số nhóm', key: 'g',
              render: (_, s) => <Checkbox.Group value={cfg.staff[s.id] ?? []} onChange={(x) => setStaff(s.id, x as GroupKey[])} options={GROUPS.map((g) => ({ value: g.key, label: g.label }))} />,
            },
            { title: 'Tỉ lệ yêu cầu', key: 'r', width: 150, render: (_, s) => <InputNumber min={0} max={100} value={cfg.ratio[s.id]} onChange={(x) => setRatio(s.id, x)} addonAfter="%" className="!w-[120px]" /> },
          ]} />
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {sums.map(({ g, sum }) => <Tag key={g.key} color={sum === 100 || !sum ? 'default' : 'orange'}>{g.label}: tổng tỉ lệ {sum}%</Tag>)}
        </div>
        <p className="mt-1 text-xs text-gray-500">Người chưa tick nhóm nào nhận khách tràn của mọi nhóm. Tỉ lệ dùng cho bảng Cân bằng số ở Dashboard và ưu tiên bù số khi chia.</p>
      </Card>
    </div>
  );
}

/* ---------------- Ngưỡng vận hành ---------------- */

const OPS_FIELDS: { name: keyof Cfg; label: string; unit: string; min: number }[] = [
  { name: 'slaSec', label: 'Service level — nhận cuộc gọi đến trong', unit: 'giây', min: 5 },
  { name: 'missedMin', label: 'Gọi lại cuộc nhỡ trong', unit: 'phút', min: 5 },
  { name: 'newLeadMin', label: 'Gọi khách mới trong', unit: 'phút', min: 1 },
  { name: 'acwMaxMin', label: 'Hoàn tất sau cuộc gọi tối đa', unit: 'phút', min: 1 },
  { name: 'callTarget', label: 'Chỉ tiêu cuộc gọi đi / NV / ngày', unit: 'cuộc', min: 1 },
  { name: 'bookTarget', label: 'Chỉ tiêu lịch đặt / NV / ngày', unit: 'lịch', min: 1 },
];

function OpsTab() {
  const { cfg, setCfg } = useTeleDash();
  const [c, setC] = useState<Cfg>(cfg);
  const stages = c.forgotStages ?? { S2: c.forgotDays, S3: c.forgotDays, S4: c.forgotDays };
  const setStage = (st: string, days: number | undefined) => {
    const next = { ...stages };
    if (days == null) delete next[st]; else next[st] = days;
    setC({ ...c, forgotStages: next });
  };
  return (
    <Card title="Ngưỡng vận hành Telesales" extra={<Button size="small" type="link" onClick={() => setC(DEFAULT_CFG)}>Khôi phục mặc định</Button>}>
      <div className="grid max-w-[720px] grid-cols-1 gap-2.5 text-[13px]">
        {OPS_FIELDS.map((f) => (
          <label key={f.name} className="grid grid-cols-[1fr_180px] items-center gap-3">
            <span>{f.label}</span>
            <InputNumber min={f.min} value={c[f.name] as number} onChange={(x) => setC({ ...c, [f.name]: x ?? f.min })} addonAfter={f.unit} />
          </label>
        ))}
        <div className="mt-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="mb-2 font-semibold">Khách không được chạm quá N ngày là bị bỏ quên — chọn từng trạng thái</div>
          {FORGOT_STAGES.map((st) => {
            const on = stages[st] != null;
            return (
              <div key={st} className="grid grid-cols-[1fr_180px] items-center gap-3 py-1">
                <Checkbox checked={on} onChange={(e) => setStage(st, e.target.checked ? c.forgotDays : undefined)}>{st}</Checkbox>
                <InputNumber min={1} disabled={!on} value={stages[st]} onChange={(x) => setStage(st, x ?? 1)} addonAfter="ngày" />
              </div>
            );
          })}
          <p className="mt-1 text-xs text-gray-500">Ngưỡng này dùng cho cả Tổng đài Telesale; CSKH sẽ dùng cùng cách cài đặt.</p>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <Button type="primary" onClick={() => { setCfg(c); message.success('Đã lưu ngưỡng vận hành'); }}>Lưu</Button>
        <Button onClick={() => setC(cfg)}>Huỷ thay đổi</Button>
      </div>
    </Card>
  );
}

/* ---------------- Chia lại khách quá hạn ---------------- */

function ReassignTab() {
  const cfg = useLeadStore((s) => s.reassignCfg);
  const chat = useLeadStore((s) => s.chatCfg);
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState<CustomerTagItem[]>([]);
  const staffs = useTeleStaffs();
  useEffect(() => { getCustomerTags({ sort: ['id,asc'] }).then((r) => { if (r.status && r.data) setTags(r.data.items); }).catch(() => undefined); }, []);
  const navigate = useNavigate();
  const list = useMemo(() => staffs.filter((s) => /^\d+$/.test(s.id)).map((s) => ({ id: Number(s.id), name: s.name })), [staffs]);
  return (
    <Card title="Chia lại khách quá hạn">
      <div className="space-y-2 text-[13px]">
        <div>Gọi điện: {cfg.enabled ? <>quá <b>{thresholdText(cfg)}</b> không tương tác → {cfg.mode === 'auto' ? 'tự chia' : 'đề xuất để Người vận hành chia'}</> : <Tag>Đang tắt</Tag>}</div>
        <div>Chat: {chat.enabled ? <>khách chờ trả lời quá <b>{thresholdText(chat)}</b> → {chat.mode === 'auto' ? 'tự chia' : 'đề xuất'}</> : <Tag>Đang tắt</Tag>}</div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="primary" icon={<Settings2 size={14} />} onClick={() => setOpen(true)}>Chỉnh cài đặt chia lại</Button>
        <Button onClick={() => navigate('/telesales/reassign-history')}>Lịch sử chia lại</Button>
      </div>
      <ReassignSettings open={open} onClose={() => setOpen(false)} tags={tags} staffs={list} pageStaffs={list} />
    </Card>
  );
}

export default function TelesaleSettings() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const tab = sp.get('tab') ?? 'groups';
  // Trang mở cho quyền xem Telesales (route), nhưng chỉ quản lý được chỉnh — như nút Ngưỡng / Chia lại ở Dashboard, Danh sách khách
  const { isSuperAdmin, hasPermission } = useAuthStore();
  const canManage = isSuperAdmin || hasPermission('telesales.config') || hasPermission('telesales.assign');
  return (
    <Layout>
      <Helmet><title>Cài đặt Telesale | Khothi</title></Helmet>
      <div className="mx-auto w-full max-w-[1400px] space-y-4 pb-24">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Cài đặt Telesale</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Chia số theo nhóm dịch vụ, ngưỡng vận hành, chia lại khách, quy tắc chuyển trạng thái — tất cả ở một chỗ</p>
        </div>
        {!canManage ? (
          <Alert type="warning" showIcon message="Chỉ quản lý Telesales được xem và chỉnh cài đặt này"
            description="Cần quyền cấu hình hoặc phân công Telesales (telesales.config / telesales.assign). Liên hệ Trưởng phòng nếu cần đổi cách chia số, ngưỡng vận hành." />
        ) : <Tabs activeKey={tab} onChange={(k) => setSp({ tab: k })} items={[
          { key: 'groups', label: 'Nhóm dịch vụ & chia số', children: <GroupsTab /> },
          { key: 'ops', label: 'Ngưỡng vận hành', children: <OpsTab /> },
          { key: 'reassign', label: 'Chia lại khách quá hạn', children: <ReassignTab /> },
          {
            key: 'stage', label: 'Chuyển trạng thái tự động',
            children: (
              <Card title="Quy tắc chuyển trạng thái S">
                <Alert type="info" showIcon message="Quy tắc chuyển trạng thái theo lịch hẹn (đến, không đến, chốt…) đang quản lý ở màn Cấu hình chuyển trạng thái." />
                <Button className="mt-3" icon={<ArrowRight size={14} />} onClick={() => navigate('/appointment-transitions')}>Mở cấu hình chuyển trạng thái</Button>
              </Card>
            ),
          },
        ]} />}
      </div>
    </Layout>
  );
}
